/**
 * THE ONE FILL PASS OVER THE LIVE DOCUMENT — shared by the Studio and the form (2026-09-14).
 *
 * Until now this effect lived only in StudioView. The Studio and the form SWAP (they never
 * mount together), so once a requester had opened Design Email and come back to Step 1,
 * App's block document — which the form's preview now renders — was frozen: change a
 * community on Step 1 and the hero updated (the rail computes that from the template) but
 * every Featured card kept the plan of the community that had just been REMOVED, until the
 * Studio was reopened and its effect ran. Driven live on dev, Jie's case (CRS · Grand Opening
 * Thank You): Cypress Preserve + Brunswick Plantation → remove Cypress → the rail still
 * showed Cypress's "Owen" card beside Brunswick's. That is her "photos are popping up
 * haphazardly". The same pass now runs wherever the document is being looked at.
 *
 * The body is StudioView's, moved verbatim: trim the pristine cards the scope cannot fill,
 * clone the last card up to one per community, then the field fill — each structural
 * change returns and lets the next pass (off the new `blocks`) do the rest, exactly as the
 * Studio's convergence has always worked.
 */
import { useEffect } from "react";
import type { AnyBlockInstance } from "../email/document";
import { isEch } from "./echRegistry";
import { amenFolderVersion } from "./echAmen";
import { useBynderMatchVersion } from "./echBynderMatch";
import { echCarryLabels } from "./echSection";
import { echFillAll, ECH_FEAT_KEY, ECH_CARDS_AUTO, echPlanCardsNeeded, echPlanCardsExcess, echLastPlanCardIdx, type DynBlock, type EchCtx } from "./echDyn";

export interface EchDocFillArgs {
  blocks: AnyBlockInstance[];
  setBlocks: (u: (b: AnyBlockInstance[]) => AnyBlockInstance[]) => void;
  onPatch: (id: string, patch: Record<string, unknown>) => void;
  makeBlock: (type: string) => AnyBlockInstance;
  echCtx: EchCtx;
  /** false while nothing should be written — e.g. the form before the Studio has ever
   *  produced a document (the rail then fills its OWN copy, see echPreviewOf) */
  enabled?: boolean;
}

export function useEchDocFill({ blocks, setBlocks, onPatch, makeBlock, echCtx, enabled = true }: EchDocFillArgs): void {
  /* THE ICON LIBRARY IS A FILL INPUT, SO IT BELONGS IN THE DEPS (2026-08-21) — the amenity
     branch resolves against /amenity-icons, which arrives asynchronously; without this the
     first fill ran against the 4-icon fallback and never re-ran. */
  const amenLibV = amenFolderVersion();
  /* a Bynder crop landing for a card photo re-runs the fill (2026-09-25, echBynderMatch) */
  const bynV = useBynderMatchVersion();
  useEffect(() => {
    if (!enabled) return;
    if (!blocks.some((b) => isEch(b.type))) return;
    /* pristine cards past what the scope can fill are removed; a touched card never is */
    const drop = echPlanCardsExcess(blocks as unknown as DynBlock[], echCtx);
    if (drop.length) {
      const gone = new Set(drop);
      setBlocks((cur) => cur.filter((b) => !gone.has(b.id)));
      return;
    }
    /* one plan card per community (cap 3) — clone the last card and stamp the set so a
       card the requester deletes stays deleted (see echPlanCardsNeeded) */
    const need = echPlanCardsNeeded(blocks as unknown as DynBlock[], echCtx);
    if (need > 0) {
      const at = echLastPlanCardIdx(blocks as unknown as DynBlock[]);
      const src = blocks[at];
      if (src) {
        const add: AnyBlockInstance[] = [];
        for (let i = 0; i < need; i++) {
          const nb = makeBlock(src.type);
          /* the clone takes the section's button labels, not the block's defaults (2026-09-18) */
          add.push({ ...nb, data: { ...nb.data, ...echCarryLabels(src.data as Record<string, unknown>, src.type), [ECH_FEAT_KEY]: String(src.data[ECH_FEAT_KEY] || "plans"), [ECH_CARDS_AUTO]: "1" } });
        }
        setBlocks((cur) => [...cur.slice(0, at + 1), ...add, ...cur.slice(at + 1)]);
        onPatch(src.id, { [ECH_CARDS_AUTO]: "1" });
        return;
      }
    }
    const patches = echFillAll(blocks, echCtx);
    for (const id of Object.keys(patches)) onPatch(id, patches[id]);
    /* `setBlocks`/`makeBlock` are only ever CALLED here, never read, and App re-creates
       them per render — listing them would re-run the whole-document fill every render */
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [blocks, echCtx, onPatch, amenLibV, bynV, enabled]);
}
